import { FC } from "react"
import {
    ConstFilterOperator,
    BinaryFilterOperator,
    UnaryFilterOperator,
    MultipleValueFilterOperator,
    isMultipleOperator,
    isUnaryOperator,
    isBinaryOperator,
    FilterExpression,
    isBinaryFilter,
    isMultipleFilter,
    isUnaryFilter,
    FeedbackMessageFilterOperator,
    isFeedbackMessageOperator,
    isFeedbackMessageFilter,
    ActionsFilterOperator,
    isActionsOperator,
    isActionsFilter
} from "../../../Models/Client/FilterExpression"

export interface PredicateInputComponentProps<V> {
    value: V
    onChange: (value?: V) => void
}

interface ConstFilterPredicateEq<V> {
    label: string
    value: V
    operator: ConstFilterOperator.eq
}

interface ConstFilterPredicateIsOneOf<V> {
    label: string
    value: V[]
    operator: ConstFilterOperator.isOneOf
}

interface UnaryFilterPredicate<V> {
    label: string
    defaultValue: V
    operator: UnaryFilterOperator
    component: FC<PredicateInputComponentProps<V>>
}

interface BinaryFilterPredicate<V> {
    label: string
    defaultValue: [V, V]
    operator: BinaryFilterOperator
    component: FC<PredicateInputComponentProps<[V, V]>>
}

interface MultipleValuePredicate<V> {
    label: string
    defaultValue: V[]
    operator: MultipleValueFilterOperator
    component: FC<PredicateInputComponentProps<V[]>>
}

interface FeedbackMessageFilterPredicate<V> {
    label: string
    defaultValue: V
    operator: FeedbackMessageFilterOperator
    component?: FC<PredicateInputComponentProps<V>>
}

interface ActionsFilterPredicate<V> {
    label: string
    defaultValue: V
    operator: ActionsFilterOperator
    component?: FC<PredicateInputComponentProps<V>>
}

export type FilterPredicate<V> =
    | ConstFilterPredicateEq<V>
    | ConstFilterPredicateIsOneOf<V>
    | UnaryFilterPredicate<V>
    | BinaryFilterPredicate<V>
    | MultipleValuePredicate<V>
    | FeedbackMessageFilterPredicate<V>
    | ActionsFilterPredicate<V>

export function isMultiplePredicate<V>(
    predicate: FilterPredicate<V>
): predicate is MultipleValuePredicate<V> {
    return isMultipleOperator(predicate.operator)
}

export function isUnaryPredicate<V>(
    predicate: FilterPredicate<V>
): predicate is UnaryFilterPredicate<V> {
    return isUnaryOperator(predicate.operator)
}

export function isBinaryPredicate<V>(
    predicate: FilterPredicate<V>
): predicate is BinaryFilterPredicate<V> {
    return isBinaryOperator(predicate.operator)
}

export function isFeedbackMessagePredicate<V>(
    predicate: FilterPredicate<V>
): predicate is FeedbackMessageFilterPredicate<V> {
    return isFeedbackMessageOperator(predicate.operator)
}

export function isActionsPredicate<V>(
    predicate: FilterPredicate<V>
): predicate is ActionsFilterPredicate<V> {
    return isActionsOperator(predicate.operator)
}

export function setPredicate<M>(
    filter: FilterExpression<M>,
    predicate: FilterPredicate<M[keyof M]>
): FilterExpression<M> {
    if (isMultiplePredicate(predicate)) {
        return {
            fieldName: filter.fieldName,
            operator: predicate.operator,
            value: isMultipleFilter(filter)
                ? filter.value
                : isUnaryFilter(filter)
                ? [filter.value]
                : isBinaryFilter(filter)
                ? [filter.value[0]]
                : filter.operator === ConstFilterOperator.eq
                ? [filter.value]
                : isFeedbackMessageFilter(filter)
                ? [filter.value]
                : isActionsFilter(filter)
                ? [filter.value]
                : filter.value
        }
    }

    if (isUnaryPredicate(predicate)) {
        return {
            fieldName: filter.fieldName,
            operator: predicate.operator,
            value: isMultipleFilter(filter)
                ? filter.value.length > 0
                    ? filter.value[0]
                    : predicate.defaultValue
                : isUnaryFilter(filter)
                ? filter.value
                : isFeedbackMessageFilter(filter)
                ? filter.value
                : isBinaryFilter(filter)
                ? filter.value[0]
                : filter.operator === ConstFilterOperator.eq
                ? filter.value
                : isActionsFilter(filter)
                ? filter.value
                : filter.value[0]
        }
    }

    if (isBinaryPredicate(predicate)) {
        return {
            fieldName: filter.fieldName,
            operator: predicate.operator,
            value: isMultipleFilter(filter)
                ? filter.value.length > 0
                    ? [filter.value[0], filter.value[0]]
                    : predicate.defaultValue
                : isUnaryFilter(filter)
                ? [filter.value, filter.value]
                : isBinaryFilter(filter)
                ? filter.value
                : filter.operator === ConstFilterOperator.eq
                ? [filter.value, filter.value]
                : isFeedbackMessageFilter(filter)
                ? [filter.value, filter.value]
                : isActionsFilter(filter)
                ? [filter.value, filter.value]
                : [filter.value[0], filter.value[0]]
        }
    }

    if (predicate.operator === ConstFilterOperator.eq) {
        return {
            fieldName: filter.fieldName,
            operator: predicate.operator,
            value: predicate.value
        }
    }

    if (isFeedbackMessagePredicate(predicate)) {
        return {
            fieldName: filter.fieldName,
            operator: predicate.operator,
            value: isMultipleFilter(filter)
                ? filter.value.length > 0
                    ? filter.value[0]
                    : predicate.defaultValue
                : isUnaryFilter(filter)
                ? filter.value
                : isFeedbackMessageFilter(filter)
                ? predicate.defaultValue
                : isBinaryFilter(filter)
                ? filter.value[0]
                : filter.operator === ConstFilterOperator.eq
                ? filter.value
                : isActionsFilter(filter)
                ? filter.value
                : filter.value[0]
        }
    }

    if (isActionsPredicate(predicate)) {
        return {
            fieldName: filter.fieldName,
            operator: predicate.operator,
            value: predicate.defaultValue
        }
    }

    return {
        fieldName: filter.fieldName,
        operator: predicate.operator,
        value: predicate.value
    }
}

export function setFieldName<M, K extends keyof M>(
    predicate: FilterPredicate<M[K]>,
    fieldName: K
): FilterExpression<M> {
    return isMultiplePredicate(predicate)
        ? {
              fieldName,
              operator: predicate.operator,
              value: predicate.defaultValue
          }
        : isUnaryPredicate(predicate)
        ? {
              fieldName,
              operator: predicate.operator,
              value: predicate.defaultValue
          }
        : isBinaryPredicate(predicate)
        ? {
              fieldName,
              operator: predicate.operator,
              value: predicate.defaultValue
          }
        : predicate.operator === ConstFilterOperator.eq
        ? {
              fieldName,
              operator: predicate.operator,
              value: predicate.value
          }
        : isFeedbackMessagePredicate(predicate)
        ? {
              fieldName,
              operator: predicate.operator,
              value: predicate.defaultValue
          }
        : isActionsPredicate(predicate)
        ? {
              fieldName,
              operator: predicate.operator,
              value: predicate.defaultValue
          }
        : {
              fieldName,
              operator: predicate.operator,
              value: predicate.value
          }
}
